import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { CONSTANTS as C } from "config";
import TableItemsDetail from "./TableItemsDetail";

const PrintItem = ({ label, value }) => (
  <div className="break-inside-avoid rounded-[10px] px-3">
    <div className="mb-1 text-[11px] text-gray-500">{label}</div>
    <div className="break-words text-[13px] font-semibold text-gray-900">
      {value || "..."}
    </div>
  </div>
);

const PrintSection = ({ title, children }) => (
  <div className="break-inside-avoid">
    <div className="mb-2 text-[15px] font-bold text-gray-900">
      {title}
    </div>
    {children}
  </div>
);

const PrintGrid = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-7 gap-2">{children}</div>
);

const Invoice = ({
  order,
  getPaymentLabel = () => {},
  getOrderTypeName = () => {},
  getOrderSourceLabel = () => {},
  getDeliveryAddress = () => {},
  getGoogleMapLink = () => {},
  getOrderTakenByLabel = () => {},
}) => {
  const { t } = useTranslation();

  if (!order) return null;

  return (
    <div className="px-2">
      <PrintSection title={t("Order Summary")}>
        <PrintGrid>
          <PrintItem label={t("Order #")} value={order?.orderNumber} />

          <PrintItem
            label={t("Customer Name")}
            value={order?.userInfo?.name}
          />

          <PrintItem
            label={t("Whatsapp Number")}
            value={order?.userInfo?.phoneNumber}
          />

          <PrintItem
            label={t("Preparation Type")}
            value={order?.isPreOrder ? t("PRE ORDER") : t("ASAP")}
          />

          <PrintItem label={t("Order Type")} value={getOrderTypeName()} />

          <PrintItem
            label={t("Store")}
            value={order?.storeInfo?.baseLanguage?.name}
          />

          <PrintItem
            label={t("Order Placed Time")}
            value={
              order?.orderPlacedTime
                ? moment
                    .unix(order.orderPlacedTime)
                    .format("DD-MM-YYYY hh:mm A")
                : "..."
            }
          />

          <PrintItem label={t("Order From")} value={getOrderSourceLabel()} />
          <PrintItem label={t("Payment")} value={getPaymentLabel()} />
        </PrintGrid>
      </PrintSection>

      {order?.orderType?.type === C.SERVICE_TYPES.DELIVERY ? (
        <PrintSection title={t("Delivery Information")}>
          <PrintGrid>
            <PrintItem
              label={t("Street Address")}
              value={order?.deliveryAddress?.street}
            />

            <PrintItem
              label={t("Full Address")}
              value={getDeliveryAddress()}
            />
          </PrintGrid>
        </PrintSection>
      ) : null}

      {order?.orderType?.type === C.SERVICE_TYPES.DINEIN ? (
        <PrintSection title={t("Dine-in")}>
          <PrintGrid>
            <PrintItem
              label={t("Selected Table")}
              value={order?.dineIn?.tableNumber}
            />
          </PrintGrid>
        </PrintSection>
      ) : null}

      {order?.orderType?.type === C.SERVICE_TYPES.PICKUP ? (
        <PrintSection title={t("Pickup")}>
          <PrintGrid>
            <PrintItem
              label={t("Vehicle Info")}
              value={order?.pickup?.vehicleInfo}
            />
          </PrintGrid>
        </PrintSection>
      ) : null}

      {order?.isPreOrder &&
      order?.orderStatus === C.orderStatus.SCHEDULED ? (
        <PrintSection title={t("Pre Order Information")}>
          <PrintGrid>
            <PrintItem
              label={t("Date")}
              value={
                order?.preOrder?.preOrderSlotStartTime
                  ? moment
                      .unix(order.preOrder.preOrderSlotStartTime)
                      .format("DD-MM-YYYY")
                  : "..."
              }
            />

            <PrintItem
              label={t("Slot")}
              value={
                order?.preOrder?.preOrderSlotStartTime &&
                order?.preOrder?.preOrderSlotEndTime
                  ? `${moment
                      .unix(order.preOrder.preOrderSlotStartTime)
                      .format("hh:mm A")} - ${moment
                      .unix(order.preOrder.preOrderSlotEndTime)
                      .format("hh:mm A")}`
                  : "..."
              }
            />
          </PrintGrid>
        </PrintSection>
      ) : null}

      {order?.discountApplied ? (
        <PrintSection title={t("Discount Info")}>
          <PrintGrid>
            <PrintItem
              label={t("Type")}
              value={t(order.discountApplied.discountType)}
            />

            <PrintItem
              label={t("Code")}
              value={order.discountApplied.discountCode}
            />
          </PrintGrid>
        </PrintSection>
      ) : null}

      {order?.orderLevelSpecialRequest?.trim()?.length > 0 ? (
        <PrintSection title={t("Special Note")}>
          <div className="break-inside-avoid px-3 py-2">
            <div className="break-words text-base font-semibold text-gray-900">
              {order.orderLevelSpecialRequest}
            </div>
          </div>
        </PrintSection>
      ) : null}

      <PrintSection title={t("Items & totals")}>
        <div className="break-inside-avoid">
          <TableItemsDetail order={order} />
        </div>
      </PrintSection>
    </div>
  );
};

export default Invoice;